import { Check, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { FIELD_MISSION_HOLD_MS } from '@groupe-reca/contracts'

export type HoldPhase = 'idle' | 'confirming' | 'success'

export interface HoldButtonProps {
  address?: string
  phase: HoldPhase
  holdMs?: number
  /** Nom accessible (défaut : confirmation du travail à l'adresse). */
  ariaLabel?: string
  /** Libellé au repos (défaut : « Maintenir 3 s »). */
  idleLabel?: string
  successLabel?: string
  /** Sous-libellé au repos (défaut : « pour confirmer »). */
  idleSub?: string
  className?: string
  disabled?: boolean
  /** Une seule fois, après un maintien continu complet. */
  onConfirm: () => void
  /** Relâché, sorti, annulé ou masqué avant la fin. */
  onCancel: () => void
}

const tickMs = 50

/**
 * Bouton vert unique de confirmation du travail (repris de RECA-balises) : maintien continu de 3 s (pointeur ou
 * Espace/Entrée). Annulé si relâché, sorti, annulé par le système, perte de focus ou page masquée. Un simple clic ne
 * confirme jamais; `stopPropagation` empêche d'ouvrir la fiche en même temps. Une seule confirmation par maintien.
 */
export function HoldButton({
  address = '',
  phase,
  holdMs = FIELD_MISSION_HOLD_MS,
  ariaLabel,
  idleLabel,
  successLabel = 'Terminée',
  idleSub = 'pour confirmer',
  className,
  disabled = false,
  onConfirm,
  onCancel,
}: HoldButtonProps) {
  const [elapsed, setElapsed] = useState<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedRef = useRef(0)
  const keyHeldRef = useRef(false)
  const onConfirmRef = useRef(onConfirm)
  const onCancelRef = useRef(onCancel)
  useEffect(() => {
    onConfirmRef.current = onConfirm
    onCancelRef.current = onCancel
  }, [onConfirm, onCancel])

  const clear = useCallback(() => {
    if (timerRef.current !== null) clearInterval(timerRef.current)
    timerRef.current = null
    setElapsed(null)
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current === null) return
    clear()
    onCancelRef.current()
  }, [clear])

  const start = useCallback(() => {
    if (timerRef.current !== null || phase !== 'idle' || disabled) return
    startedRef.current = Date.now()
    setElapsed(0)
    timerRef.current = setInterval(() => {
      const value = Date.now() - startedRef.current
      if (value >= holdMs) {
        clear()
        onConfirmRef.current()
        return
      }
      setElapsed(value)
    }, tickMs)
  }, [clear, holdMs, phase, disabled])

  useEffect(() => {
    function onHidden() {
      if (document.hidden) cancel()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('blur', cancel)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('blur', cancel)
    }
  }, [cancel])

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearInterval(timerRef.current)
    },
    [],
  )

  const holding = elapsed !== null
  const remaining = holding ? Math.max(holdMs - elapsed, 0) : holdMs
  const busy = phase !== 'idle'
  const label =
    phase === 'confirming'
      ? 'Confirmation…'
      : phase === 'success'
        ? successLabel
        : (idleLabel ?? `Maintenir ${String(Math.ceil(holdMs / 1000))} s`)
  const sub = holding
    ? `${(remaining / 1000).toFixed(1).replace('.', ',')} s`
    : phase === 'idle'
      ? idleSub
      : ''

  return (
    <button
      type="button"
      className={`field-hold${className ? ` ${className}` : ''}${holding ? ' is-holding' : ''}${busy ? ' is-busy' : ''}`}
      aria-label={
        ariaLabel ?? `Confirmer le travail au ${address} — maintenir ${String(Math.round(holdMs / 1000))} secondes`
      }
      aria-disabled={busy}
      disabled={disabled}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        event.preventDefault()
        event.stopPropagation()
        if (typeof event.currentTarget.setPointerCapture === 'function')
          event.currentTarget.setPointerCapture(event.pointerId)
        start()
      }}
      onPointerUp={(event) => {
        if (typeof event.currentTarget.releasePointerCapture === 'function')
          event.currentTarget.releasePointerCapture(event.pointerId)
        cancel()
      }}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onBlur={cancel}
      onContextMenu={(event) => {
        event.preventDefault()
      }}
      onKeyDown={(event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return
        event.preventDefault()
        event.stopPropagation()
        if (keyHeldRef.current) return
        keyHeldRef.current = true
        start()
      }}
      onKeyUp={(event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return
        keyHeldRef.current = false
        cancel()
      }}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
    >
      <span
        className="field-hold__fill"
        style={{ width: holding ? `${String((elapsed / holdMs) * 100)}%` : '0%' }}
      />
      <span className="field-hold__content">
        {phase === 'confirming' ? (
          <Loader2 className="field-spin" aria-hidden="true" />
        ) : (
          <Check aria-hidden="true" />
        )}
        <span className="field-hold__label">{label}</span>
        {sub ? <span className="field-hold__sub">{sub}</span> : null}
      </span>
    </button>
  )
}
